import React, { ErrorInfo, ReactNode } from "react";

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // In production, we log this to our client authentication & telemetry logging system
    console.error("Uncaught runtime application error recorded by Teralumax Gatekeeper:", error, errorInfo);
    
    // Update state to include detailed error info for diagnostic analysis
    this.setState({
      error,
      errorInfo
    });

    // Option to dispatch internal telemetry triggers or report to administrative log
    try {
      const activeLogs = localStorage.getItem("teralumax_auth_logs");
      let logs = [];
      if (activeLogs) {
        logs = JSON.parse(activeLogs);
      }
      logs.unshift({
        id: Date.now().toString(),
        timestamp: new Date().toLocaleTimeString(),
        type: "failure",
        ip: "SYSTEM_MONITOR",
        details: `FATAL CLIENT CORE EXCEPTION: ${error.message.slice(0, 80)}`
      });
      localStorage.setItem("teralumax_auth_logs", JSON.stringify(logs.slice(0, 200)));
    } catch (e) {
      // Fail silently to prevent cascading failures
    }
  }

  private handleReset = () => {
    // Clear dynamic configs or variables if needed, then reload
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = "/";
  };

  private handleHardReload = () => {
    // Force a full clean reload of cache assets to resolve deployment synchronization hiccups
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => registration.unregister());
      });
    }
    caches.keys().then((names) => {
      names.forEach((name) => caches.delete(name));
    });
    localStorage.removeItem("teralumax_allowed_ips"); // clear potential IP lockout state corruption
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-[#111827] text-white flex flex-col items-center justify-center p-6 font-sans antialiased selection:bg-indigo-500 selection:text-white">
          <div className="w-full max-w-xl bg-[#1f2937] border border-gray-800 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
            {/* Ambient visual gradient accent */}
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500" />
            
            {/* Header Badge */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-red-950/40 border border-red-500/30 flex items-center justify-center text-red-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h1 className="font-display font-medium text-lg tracking-tight text-white">TERALUMAX Core Protection</h1>
                <p className="text-xs font-mono text-gray-400 tracking-wider">GATEKEEPER SECURITY SHIELD ACTIVE</p>
              </div>
            </div>

            {/* Error Message */}
            <div className="mb-6 p-4 rounded-xl bg-gray-900/60 border border-gray-800 font-mono text-xs text-red-300 overflow-x-auto select-all leading-relaxed">
              <span className="text-gray-500 font-bold mr-2">CONTEXT:</span> Runtime Core Exception Intercepted
              <div className="mt-2 text-white font-semibold">
                {this.state.error?.toString() || "Unknown terminal process exception"}
              </div>
            </div>

            <p className="text-sm text-gray-300 leading-relaxed mb-6">
              An unexpected validation anomaly or script lock occurred in the client application. Security systems successfully isolated this state to prevent session data or configuration corruption.
            </p>

            {/* Diagnostics details (collapsible micro accordion) */}
            {this.state.errorInfo && (
              <details className="group mb-6 border border-gray-800 rounded-xl bg-black/20 overflow-hidden">
                <summary className="flex items-center justify-between p-3 text-xs font-mono text-gray-400 cursor-pointer hover:bg-gray-800/40 transition-colors select-none font-semibold">
                  <span>SYSTEM CALL DIAGNOSTIC LOGS</span>
                  <span className="text-[10px] group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-3 border-t border-gray-800 font-mono text-[10px] text-gray-500 overflow-y-auto max-h-40 leading-normal select-all">
                  {this.state.errorInfo.componentStack}
                </div>
              </details>
            )}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <button
                id="error_boundary_retry_btn"
                onClick={this.handleReset}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white font-medium text-sm transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-lg shadow-indigo-600/10 flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Retry Application Core</span>
              </button>
              <button
                id="error_boundary_hard_reload_btn"
                onClick={this.handleHardReload}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-transparent border border-gray-700 hover:bg-gray-800 active:bg-gray-900 text-gray-300 hover:text-white font-medium text-sm transition-all focus:outline-none focus:ring-2 focus:ring-gray-600 flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Hard Reset & Clear Cache</span>
              </button>
            </div>

            <div className="mt-6 pt-5 border-t border-gray-800/60 flex items-center justify-between text-[10px] font-mono text-gray-500">
              <span>ACTIVE GATEKEEPER MODE</span>
              <span>DEPLOY STATS: SECURE</span>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
